import type { CfeDocument, CfeGenerateResult } from '../types/cfe';

export interface IssueTask {
  id: string;
  label: string;
  status: 'running' | 'success' | 'error';
  detail?: string;
}

export interface ReceiptIssuePlan {
  recibo_id: number;
  numero_recibo: string;
  required_types: number[];
  documents: CfeDocument[];
}

interface IssueApi {
  load: (id: number) => Promise<ReceiptIssuePlan>;
  generate: (id: number) => Promise<CfeGenerateResult>;
  document: (id: number) => Promise<CfeDocument>;
  confirm: (id: number) => Promise<{ success: boolean; message: string }>;
}

const flag = (value: unknown) => value === true || value === 1 || value === '1';

/** Keeps saved IDs and completed steps when a later fiscal operation is retried. */
export class ReceiptIssuance {
  ids: number[] = [];
  private tasks = new Map<string, IssueTask>();

  constructor(
    private api: IssueApi,
    private publish: (tasks: IssueTask[]) => void,
    private errorMessage: (error: unknown) => string,
    private signal: AbortSignal,
  ) {}

  private update(task: IssueTask) {
    this.signal.throwIfAborted();
    this.tasks.set(task.id, task);
    this.publish([...this.tasks.values()]);
  }

  private async step<T>(id: string, label: string, action: () => Promise<T>): Promise<T> {
    this.update({ id, label, status: 'running' });
    try {
      const result = await action();
      this.update({ id, label, status: 'success' });
      return result;
    } catch (error) {
      this.update({ id, label, status: 'error', detail: this.errorMessage(error) });
      throw error;
    }
  }

  private wait() {
    return new Promise<void>((resolve, reject) => {
      this.signal.throwIfAborted();
      const abort = () => { clearTimeout(timer); reject(this.signal.reason); };
      const timer = setTimeout(() => { this.signal.removeEventListener('abort', abort); resolve(); }, 5000);
      this.signal.addEventListener('abort', abort, { once: true });
    });
  }

  async run(create: () => Promise<number[]>): Promise<void> {
    this.signal.throwIfAborted();
    if (this.ids.length === 0) {
      await this.step('save', 'Guardando recibo...', async () => {
        const ids = await create();
        if (!ids.length || ids.some(id => !Number.isSafeInteger(id) || id <= 0)) {
          throw new Error('No se pudieron identificar los recibos guardados. Revise el listado antes de volver a crearlos.');
        }
        this.ids = [...new Set(ids)];
      });
    }

    for (const id of this.ids) {
      let plan = await this.step(`plan-${id}`, this.ids.length > 1 ? `Verificando recibo #${id}...` : 'Verificando recibo guardado...', async () => {
        const plan = await this.api.load(id);
        if (Number(plan?.recibo_id) !== id || !Array.isArray(plan.required_types) || !Array.isArray(plan.documents)
          || plan.required_types.some(type => ![101, 182].includes(type))) {
          throw new Error('No se pudieron verificar los comprobantes requeridos.');
        }
        return plan;
      });
      if (plan.required_types.length === 0) continue;
      const suffix = this.ids.length > 1 ? ` · ${plan.numero_recibo || `Recibo #${id}`}` : '';
      const generated = (candidate: ReceiptIssuePlan) => candidate.required_types.every(type =>
        candidate.documents.some(doc => Number(doc.cfe_tipo_code) === type && !doc.original_cfe_id && flag(doc.cfe_firmado_ok)));

      plan = await this.step(`generate-${id}`, `Generando CFE...${suffix}`, async () => {
        if (generated(plan)) return plan;
        const result = await this.api.generate(id);
        if (result.success !== true) throw new Error(result.message || 'No se pudieron generar todos los CFE.');
        const refreshed = await this.api.load(id);
        // Verify the original requirements too; an empty/malformed response is never success.
        if (!refreshed || !Array.isArray(refreshed.documents) || !generated({ ...plan, documents: refreshed.documents })) {
          throw new Error('Faltan CFE generados con firma válida. Puede reintentar sin volver a guardar el recibo.');
        }
        return { ...plan, documents: refreshed.documents };
      });

      for (const type of plan.required_types) {
        const doc = plan.documents.find(doc => Number(doc.cfe_tipo_code) === type && !doc.original_cfe_id && flag(doc.cfe_firmado_ok))!;
        const taskId = `confirm-${doc.id}`;
        const label = `Confirmando firma de ${type === 101 ? 'E-Ticket' : 'E-Resguardo'}...${suffix}`;
        if (flag(doc.cfe_confirmado)) {
          this.update({ id: taskId, label, status: 'success' });
          continue;
        }
        await this.step(taskId, label, async () => {
          for (let attempt = 1; attempt <= 5; attempt++) {
            this.update({ id: taskId, label, status: 'running', detail: `Intento ${attempt} de 5` });
            try {
              // A previous response may have been lost after the server confirmed the CFE.
              const current = await this.api.document(Number(doc.id));
              this.signal.throwIfAborted();
              if (flag(current.cfe_confirmado)) return;
              const result = await this.api.confirm(Number(doc.id));
              if (result.success !== true) throw new Error(result.message || 'No se pudo confirmar la firma.');
              const saved = await this.api.document(Number(doc.id));
              if (!flag(saved.cfe_confirmado)) throw new Error('La confirmación todavía no figura guardada.');
              return;
            } catch (error) {
              this.signal.throwIfAborted();
              if (attempt === 5) throw error;
              this.update({ id: taskId, label, status: 'running',
                detail: `${this.errorMessage(error)} Reintentando en 5 segundos (intento ${attempt + 1} de 5).` });
              await this.wait();
            }
          }
        });
      }
    }
  }
}
